import { z } from 'zod'

/**
 * Onboarding config: the versioned content document that drives the first-run
 * interview, persona scoring, provisioning, and the first-week checklist.
 *
 * Spec: docs/superpowers/specs/2026-08-21-onboarding-experience-design.md (D5).
 * The document is authored content (eventually admin-managed in edge-admin-api,
 * versioned); the frontend ships a bundled fallback in default-config.ts. The
 * schema validates BOTH sources, so a bad server config falls back rather than
 * breaking first-run.
 */

export const OnboardingOptionSchema = z.object({
  id: z.string().min(1),
  label: z.string().min(1),
  /** Persona → weight added when this option is chosen. Keys validated below. */
  scores: z.record(z.number()).optional().default({}),
})

export const OnboardingQuestionSchema = z.object({
  id: z.string().min(1),
  prompt: z.string().min(1),
  description: z.string().optional(),
  /** Optional questions can be skipped without affecting completion. */
  optional: z.boolean().optional().default(false),
  options: z.array(OnboardingOptionSchema).min(2),
  /** Skip this question when an EARLIER question was answered with optionId. */
  skipIf: z
    .object({ questionId: z.string().min(1), optionId: z.string().min(1) })
    .nullable()
    .optional()
    .default(null),
})

export const OnboardingSeedSchema = z.object({
  /** Tickers for the starter watchlist (empty = no watchlist seeding). */
  watchlist: z.array(z.string()).optional().default([]),
  /** Ticker the first chart opens on (null = app default). */
  chartTicker: z.string().nullable().optional().default(null),
})

export const OnboardingProvisioningSchema = z.object({
  /** Workspace preset id (matches presetId in features/dashboard/presets). */
  presetId: z.string().min(1),
  /** Learn track auto-assigned on completion (features/learn/tracks.ts id). */
  trackId: z.string().nullable().optional().default(null),
  /** First-week checklist id (must exist in checklists when set). */
  checklistId: z.string().nullable().optional().default(null),
  /**
   * Route to land on after provisioning instead of the dashboard (e.g. the
   * backtest strategy library for strategists, once its catalog ships).
   */
  landingRoute: z.string().nullable().optional().default(null),
  seed: OnboardingSeedSchema.optional().default({ watchlist: [], chartTicker: null }),
})

export const OnboardingChecklistItemSchema = z.object({
  id: z.string().min(1),
  label: z.string().min(1),
  /** KB walkthrough slug launched on click; null = plain action item. */
  walkthrough: z.string().nullable().optional().default(null),
  /** Panel type hosting the walkthrough (learn-launch opens it first). */
  panel: z.string().nullable().optional().default(null),
  /**
   * App event that auto-progresses this item (e.g. 'watchlist-add',
   * 'companion-ask'); null = walkthrough completion or manual tick only.
   */
  signal: z.string().nullable().optional().default(null),
  /** Signal count needed to auto-complete (e.g. add 3 tickers). */
  target: z.number().int().positive().optional().default(1),
})

export const OnboardingChecklistSchema = z.object({
  items: z.array(OnboardingChecklistItemSchema).min(1),
})

export const OnboardingConfigSchema = z
  .object({
    version: z.number().int().positive(),
    personas: z.array(z.string().min(1)).min(1),
    questions: z.array(OnboardingQuestionSchema).min(1),
    /** Persona → provisioning bundle. Every persona must have an entry. */
    provisioning: z.record(OnboardingProvisioningSchema),
    checklists: z.record(OnboardingChecklistSchema).optional().default({}),
    /**
     * First-touch tour offers, keyed by panel kind (e.g. 'scanner'): shown
     * once on that panel's first open (spec D8 contextual tips).
     */
    tips: z
      .record(
        z.object({
          walkthrough: z.string().min(1),
          panel: z.string().min(1),
          label: z.string().min(1),
        }),
      )
      .optional()
      .default({}),
  })
  .superRefine((cfg, ctx) => {
    const personas = new Set(cfg.personas)
    cfg.questions.forEach((q, qi) => {
      for (const opt of q.options) {
        for (const key of Object.keys(opt.scores)) {
          if (!personas.has(key)) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              path: ['questions', qi, 'options'],
              message: `option "${opt.id}" scores unknown persona "${key}"`,
            })
          }
        }
      }
      if (q.skipIf) {
        const earlier = cfg.questions.slice(0, qi)
        const ref = earlier.find((e) => e.id === q.skipIf!.questionId)
        if (!ref) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['questions', qi, 'skipIf'],
            message: `skipIf must reference an earlier question (got "${q.skipIf.questionId}")`,
          })
        } else if (!ref.options.some((o) => o.id === q.skipIf!.optionId)) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['questions', qi, 'skipIf'],
            message: `skipIf option "${q.skipIf.optionId}" does not exist on question "${ref.id}"`,
          })
        }
      }
    })
    for (const persona of cfg.personas) {
      if (!cfg.provisioning[persona]) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['provisioning'],
          message: `persona "${persona}" has no provisioning bundle`,
        })
      }
    }
    for (const [persona, bundle] of Object.entries(cfg.provisioning)) {
      if (bundle.checklistId && !cfg.checklists[bundle.checklistId]) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['provisioning', persona, 'checklistId'],
          message: `checklist "${bundle.checklistId}" is not defined in checklists`,
        })
      }
    }
  })

export type OnboardingOption = z.infer<typeof OnboardingOptionSchema>
export type OnboardingQuestion = z.infer<typeof OnboardingQuestionSchema>
export type OnboardingProvisioning = z.infer<typeof OnboardingProvisioningSchema>
export type OnboardingChecklistItem = z.infer<typeof OnboardingChecklistItemSchema>
export type OnboardingChecklist = z.infer<typeof OnboardingChecklistSchema>
export type OnboardingConfig = z.infer<typeof OnboardingConfigSchema>
/** Pre-parse shape (defaults optional): what authored config literals satisfy. */
export type OnboardingConfigInput = z.input<typeof OnboardingConfigSchema>
